import {
  Alert,
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  SxProps,
} from '@mui/material';
import { Body, H6 } from 'components/Typography';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangeCardIcon, GoogleWalletEN, GoogleWalletFR, AppleWalletFR, AppleWalletEN } from '../../../assets/icons';
import { ArrowForwardIos } from '@mui/icons-material';
import FamiliplusProfile from '../FamiliplusProfile';
import Transaction from 'services/transaction/Transaction';
import useAuth0 from 'common/hooks/useAuth0';
import { GeneratePassForGooglePay } from 'services/WalletService';
import { useNavigate } from 'common/hooks/useNavigate';
import { GeneratePassForApplePass } from 'services/WalletService';
import { isIOS, isMacOs } from 'react-device-detect';
import FamiliplusLongVersion from '../../../images/FamiliplusLongVersion.svg';

interface MenuItem {
  icon: JSX.Element;
  label: string;
  onClick?: () => void;
}

type MenuItems = MenuItem[];

function Container(props: { style?: CSSProperties; children: React.ReactNode; sx?: SxProps }): JSX.Element {
  return (
    <Paper elevation={2} style={props.style} sx={{ p: 2, mb: 3, color: '#002C53', ...props.sx }}>
      {props.children}
    </Paper>
  );
}

function FamiliplusProfileCardInfos(props: {
  fullName: string;
  cardNumber: string;
  numberOfPoints: number;
  valueOfPoints: number;
}) {
  const { t } = useTranslation('familiplusPage');
  const pointsValue = (Math.round(props.valueOfPoints * 100) / 100).toFixed(2);

  return (
    <Container
      style={{
        backgroundColor: 'white',
        borderWidth: '8px',
        borderColor: '#FDA9FF',
        borderStyle: 'solid',
        borderRadius: '12px',
        height: '221px',
        marginBottom: '24px',
      }}>
      <Grid
        id="familiplusProfileCardInfos"
        container
        direction="column"
        justifyContent="space-between"
        sx={{ height: '100%' }}>
        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <img data-cy="fCardLogo" src={FamiliplusLongVersion} width={'132px'} alt="Familiplus" />
            </Grid>
            <Grid item>
              <H6 data-cy="fullName" sx={{ mb: '3px' }}>
                <strong>{props.fullName}</strong>
              </H6>
              <Box data-cy="cardNumber" sx={{ color: (theme) => theme.palette.primary.main }}>
                {props.cardNumber}
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <H6 data-cy="numberOfPoints">
            <strong>{t('cardMember.nbrPointsShort', { count: props.numberOfPoints })}</strong>
          </H6>
          <Body data-cy="pointsValue">{t('cardMember.valueOfPoints', { amount: pointsValue })}</Body>
        </Grid>
      </Grid>
    </Container>
  );
}

const FamiliplusProfilePoints = (props: { nbrPoints: number; nbrDays: number }): JSX.Element => {
  const { t } = useTranslation('familiplusPage');

  return (
    <Container
      style={{
        backgroundColor: 'white',
        borderWidth: '8px',
        borderColor: '#FDA9FF',
        borderStyle: 'solid',
        borderRadius: '12px',
      }}>
      <Grid id="FamiliplusProfilePoints" container direction="row" justifyContent="space-between" alignItems="center">
        <Grid xs={6} md={8} item>
          <Box>
            <Body data-cy="pointsFamiliplus" sx={{ textTransform: 'uppercase' }}>
              <strong>{t('cardMember.pointsFamiliplus')}</strong>
            </Body>
            <Body data-cy="youHaveHarvested">{t('cardMember.youHaveHarvested')}</Body>
            <Box sx={{ mt: 3 }}>
              <H6 data-cy="nbrPoints">
                <strong>{t('cardMember.nbrPoints', { count: Math.floor(props.nbrPoints) })}</strong>
              </H6>
              <Body data-cy="inTheLastXDays">{t('cardMember.inTheLastXDays', { count: props.nbrDays })}</Body>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

const FamiliplusCardActionButtons = (): JSX.Element => {
  const localize = (key: string, obj?: Record<string, string>) => t(`cardMember.${key}`, obj ?? {});
  const [generatePassError, setGeneratePassError] = useState(false);
  const { t, i18n } = useTranslation(['familiplusPage', 'component']);
  const navigate = useNavigate();
  const { user } = useAuth0();

  const HandleGenerateGooglePass = () => {
    setGeneratePassError(false);
    return GeneratePassForGooglePay(user?.session_id).then((generatePass) => {
      if (generatePass != undefined && generatePass.status == 201) {
        window.open(
          `https://pay.google.com/gp/v/save/${generatePass.data.EncodedPass}?hl=${
            i18n.resolvedLanguage == 'fr' ? 'fr_CA' : 'en_US'
          }`,
          '_blank'
        );
      } else {
        setGeneratePassError(true);
      }
    });
  };

  const HandleGenerateApplePass = () => {
    if (isIOS || isMacOs) {
      setGeneratePassError(false);
      return GeneratePassForApplePass(user?.session_id).then((generatePass) => {
        if (generatePass != undefined && generatePass.status == 201) {
          const content = Uint8Array.from(atob(generatePass.data.FileContents), (c) => c.charCodeAt(0));
          const blob = new Blob([content], { type: generatePass.data.ContentType || 'application/vnd.apple.pkpass' });

          const anchor = document.createElement('a');
          anchor.style.display = 'none';

          const blobURL = window.URL.createObjectURL(blob);
          anchor.href = blobURL;
          anchor.setAttribute('download', generatePass.data.FileDownloadName || 'familiprix.pkpass');
          // If browser doesn't support HTML5 download attribute
          if (typeof anchor.download === 'undefined') {
            anchor.setAttribute('target', '_blank');
          }
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          // Requires delay for Firefox
          setTimeout(() => {
            // Must revoke ObjectURL after using it
            window.URL.revokeObjectURL(blobURL);
          }, 100);
        } else {
          setGeneratePassError(true);
        }
      });
    }
  };

  const menusItems: MenuItems = [
    {
      icon: <ChangeCardIcon width={'24px'} height="24px" color={'#668098'} />,
      label: localize('changeMyCard'),
      onClick: () =>
        navigate(
          `/${i18n.resolvedLanguage}/${
            i18n.resolvedLanguage == 'fr' ? 'Compte/Familiplus/Changer-Ma-Carte' : 'Account/Familiplus/Change-My-Card'
          }`
        ),
    },
  ];

  return (
    <>
      {generatePassError && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('Errors.GenericError', { ns: 'component' })}
        </Alert>
      )}
      <Container sx={{ p: 0, marginBottom: '10px !important' }}>
        <List id="FamiliplusCardActionButtons" component="nav" sx={{ p: 1 }} disablePadding>
          {menusItems.map((item: MenuItem, index) => (
            <ListItemButton
              data-cy="MenuItem"
              key={`MenuItem${index}`}
              onClick={item.onClick}
              divider={index != menusItems.length - 1}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={<b>{item.label}</b>}
                primaryTypographyProps={{
                  color: 'primary',
                  variant: 'body2',
                  fontWeight: '500',
                }}
              />
              <ArrowForwardIos sx={{ fontSize: 15, color: '#306CE1' }} />
            </ListItemButton>
          ))}
        </List>
      </Container>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ cursor: isIOS || isMacOs ? 'pointer' : 'inherit' }} onClick={HandleGenerateApplePass}>
          {i18n.resolvedLanguage == 'fr' ? <AppleWalletFR width="160px" /> : <AppleWalletEN width="160px" />}
        </Box>
        <Box sx={{ cursor: 'pointer', mt: '3px' }} onClick={HandleGenerateGooglePass}>
          {i18n.resolvedLanguage == 'fr' ? <GoogleWalletFR width="160px" /> : <GoogleWalletEN width="160px" />}
        </Box>
      </Box>
    </>
  );
};

export default function CardMember(props: {
  profile: FamiliplusProfile;
  transactions: Transaction[] | undefined;
}): JSX.Element {
  const today = new Date();
  const oneMonthAgo = new Date(new Date().setDate(today.getDate() - 30));
  const totalPoints =
    props.transactions
      ?.filter(
        (item: Transaction) => new Date(item.Date) > oneMonthAgo && item.Accumulation && item.TransactionType != 33
      )
      .reduce((sum, current) => sum + current.NbPoints, 0) ?? 0;

  return (
    <Box id="CardMember">
      <FamiliplusProfileCardInfos
        fullName={props.profile.Firstname + ' ' + props.profile.Lastname}
        cardNumber={props.profile.Card!.CardNumber}
        numberOfPoints={props.profile.Card!.Points}
        valueOfPoints={props.profile.Card!.CashValue}
      />
      <FamiliplusProfilePoints nbrDays={30} nbrPoints={totalPoints} />
      <FamiliplusCardActionButtons />
    </Box>
  );
}
